import React, { useState, useEffect } from 'react';
import { setDoc, doc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { ChevronDown, Eye, EyeOff, ArrowLeft, Lock, ExternalLink } from 'lucide-react';
import { db } from '../../src/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import './RegisterUser.css';

const countries = [
  { code: '+57', name: 'Colombia', flag: '🇨🇴' },
  { code: '+1', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+52', name: 'México', flag: '🇲🇽' },
  { code: '+34', name: 'España', flag: '🇪🇸' },
  { code: '+54', name: 'Argentina', flag: '🇦🇷' },
  { code: '+56', name: 'Chile', flag: '🇨🇱' },
  { code: '+51', name: 'Perú', flag: '🇵🇪' },
  { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
  { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
  { code: '+55', name: 'Brasil', flag: '🇧🇷' },
  { code: '+502', name: 'Guatemala', flag: '🇬🇹' },
  { code: '+504', name: 'Honduras', flag: '🇭🇳' },
  { code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
  { code: '+507', name: 'Panamá', flag: '🇵🇦' },
  { code: '+44', name: 'Reino Unido', flag: '🇬🇧' },
  { code: '+33', name: 'Francia', flag: '🇫🇷' },
  { code: '+49', name: 'Alemania', flag: '🇩🇪' },
  { code: '+39', name: 'Italia', flag: '🇮🇹' },
];

function RegisterUser() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
  const [acceptedTermsConditions, setAcceptedTermsConditions] = useState(false);

  const navigate = useNavigate();
  
  const navigateToLoginUser = () => {
    navigate('/login-user');
  };

  const auth = getAuth();

  // URLs de las políticas - reemplaza estas URLs con las tuyas
  const PRIVACY_POLICY_URL = "https://www.freeprivacypolicy.com/live/96d8f04b-b66f-4265-8dc1-49c3edb11aef";
  const TERMS_CONDITIONS_URL = "https://drive.google.com/file/d/1aESo-IebajZ8BaTIrkh4LPeCuOd7NuU8/view?usp=drive_link";

  useEffect(() => {
    if (messageType === 'success') {
      const timer = setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000); 
      return () => clearTimeout(timer);
    }
  }, [messageType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !name || !selectedCountry) {
      setMessage('Por favor completa todos los campos');
      setMessageType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Las contraseñas no coinciden');
      setMessageType('error');
      return;
    }

    if (password.length < 6) {
      setMessage('La contraseña debe tener al menos 6 caracteres');
      setMessageType('error');
      return;
    }

    if (!phoneNumber) {
      setMessage('Ingresa un número de celular');
      setMessageType('error');
      return;
    }

    if (!acceptedPrivacyPolicy) {
      setMessage('Debes aceptar la política de privacidad');
      setMessageType('error');
      return;
    }

    if (!acceptedTermsConditions) {
      setMessage('Debes aceptar los términos y condiciones');
      setMessageType('error');
      return;
    }

    setIsLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await setDoc(doc(db, 'Usuarios', user.uid), {
        uid: user.uid,
        email: user.email,
        name,
        country: selectedCountry.code,
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        rol: 'usuario',
        createdAt: new Date(),
        acceptedPrivacyPolicy: true,
        acceptedTermsConditions: true,
        acceptanceDate: new Date()
      });

      setMessage('¡Registro exitoso! Bienvenido a la plataforma');
      setMessageType('success');

      // Limpiar formularios
      setEmail('');
      setName('');
      setSelectedCountry(countries[0]);
      setPhoneNumber('');
      setPassword('');
      setConfirmPassword('');
      setAcceptedPrivacyPolicy(false);
      setAcceptedTermsConditions(false);

      // Redirigir a login
      setTimeout(() => {
        navigate('/login-user');
      }, 1500);

    } catch (error) {
      console.error('Error al registrar:', error);
      setMessage(error.message || 'Error al registrar. Inténtalo de nuevo');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 10) {
      setPhoneNumber(formatted);
    }
  };

  const clearMessage = () => {
    setMessage('');
    setMessageType('');
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
  };

  const openPrivacyPolicy = () => {
    window.open(PRIVACY_POLICY_URL, '_blank', 'noopener,noreferrer');
  };

  const openTermsConditions = () => {
    window.open(TERMS_CONDITIONS_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="registerUser-container">
      <div className="form-wrapper">
        <button className="back-button" onClick={handleGoBack}>
          <ArrowLeft size={20} />
          <span>Regresar</span>
        </button>

        <div className="logo-section">
          <img src={require('../assets/logo.png')} alt="Logo de Bearmax" className="company-logo" />
        </div>

        <h1 className="form-title">Registrate</h1>
        <p className="form-subtitle">Crea tu cuenta de usuario</p>

        <form className="form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Correo electrónico</label>
            <input 
              type="email" 
              placeholder="usuario@correo.com"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </div>

          <div className="form-group">
            <label>Dinos como te podemos llamar</label>
            <input 
              type="text" 
              placeholder="Ejemplo: Alejandro"
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              required 
            />
          </div>

          <div className="form-group">
            <label>País</label>
            <div className="dropdown">
              <button 
                type="button" 
                className="dropdown-button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              >
                <div className="dropdown-selected">
                  <span className="flag">{selectedCountry.flag}</span>
                  <span className="country-name">{selectedCountry.name}</span>
                  <span className="country-code">({selectedCountry.code})</span>
                </div>
                <ChevronDown size={20} className={`dropdown-icon ${showCountryDropdown ? 'rotated' : ''}`} />
              </button>
              
              {showCountryDropdown && (
                <ul className="dropdown-menu">
                  {countries.map((country, index) => (
                    <li 
                      key={index} 
                      className="dropdown-item"
                      onClick={() => handleCountrySelect(country)}
                    >
                      <span className="flag">{country.flag}</span>
                      <span className="country-name">{country.name}</span>
                      <span className="country-code">({country.code})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Número de celular</label>
            <div className="phone-input-wrapper">
              <div className="country-prefix">
                <span className="flag">{selectedCountry.flag}</span>
                <span className="code">{selectedCountry.code}</span>
              </div>
              <input 
                type="tel" 
                placeholder="Ejemplo: 3001234567"
                value={phoneNumber} 
                onChange={handlePhoneChange} 
                className="phone-input"
                required 
              />
            </div>
          </div>

          <div className="form-group">
            <label>Contraseña</label>
            <div className="password-wrapper">
              <input 
                type={showPassword ? 'text' : 'password'} 
                placeholder="Mínimo 6 caracteres"
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Confirmar contraseña</label>
            <div className="password-wrapper">
              <input 
                type={showConfirmPassword ? 'text' : 'password'} 
                placeholder="Repite la contraseña"
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                required 
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Checkboxes para políticas */}
          <div className="policies-section">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={acceptedPrivacyPolicy}
                  onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">
                  Acepto la{' '}
                  <button
                    type="button"
                    className="policy-link"
                    onClick={openPrivacyPolicy}
                  >
                    política de privacidad
                    <ExternalLink size={12} />
                  </button>
                </span>
              </label>
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={acceptedTermsConditions}
                  onChange={(e) => setAcceptedTermsConditions(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">
                  Acepto los{' '}
                  <button
                    type="button"
                    className="policy-link"
                    onClick={openTermsConditions}
                  >
                    términos y condiciones
                    <ExternalLink size={12} />
                  </button>
                </span>
              </label>
            </div>
          </div>

          {message && (
            <div className={`message ${messageType}`}>
              <span className="message-text">{message}</span>
              <button 
                type="button"
                className="message-close"
                onClick={clearMessage}
              >
                ×
              </button>
            </div>
          )}

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="spinner"></div>
                <span>Registrando...</span>
              </>
            ) : (
              'Crear Usuario'
            )}
          </button>
          
          <button
            type="button"
            className="login-button"
            onClick={navigateToLoginUser}
          >
            Ya tengo una cuenta
          </button>
        </form>

        <div className="security-note">
          <Lock size={12} />
          <span>Tus datos están protegidos con nosotros</span>
        </div>
      </div>
    </div>
  );
}

export default RegisterUser;